import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Divider,
  CircularProgress,
  Switch,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import IntegrationService from "../../services/integrations/Integrations";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { useQueryClient } from "@tanstack/react-query";
import CustomConfirmation from "../Common/CustomConfirmation";
import { useBeforeUnload, useLocation } from "react-router-dom";

type Frequency = "daily" | "weekly" | "none";
type TimePreferenceKey = "09:00:00" | "12:00:00" | "18:00:00";
type TaskKey = "completedTask" | "overDueTask" | "upcomingTask" | "overdueescalation";

function normalizeSettings(data: any) {
  const frequency = data.frequency;

  const timePreference: Record<TimePreferenceKey, boolean> = {
    "09:00:00": false,
    "12:00:00": false,
    "18:00:00": false,
  };

  const notificationType: Record<TaskKey, boolean> = {
    completedTask: false,
    overDueTask: false,
    upcomingTask: false,
    overdueescalation: false,
  };

  let frequencyDay = "monday";

  if (frequency === "daily") {
    (["completedTask", "overDueTask", "upcomingTask"] as TaskKey[]).forEach((task) => {
        const block = data[task];

        notificationType[task] = block?.isEnabled || false;

        (block?.frequencyTime || []).forEach((t: string) => {
        const normalized = `${t}:00`; // convert "09:00" → "09:00:00"

        if (normalized in timePreference) {
            timePreference[normalized as TimePreferenceKey] = true;
        }
        });
    });
  }

  // WEEKLY
  else if (frequency === "weekly") {
    (["completedTask", "overDueTask", "upcomingTask"] as TaskKey[]).forEach((task) => {
      const block = data[task];
      const daysObj = block?.days;

      if (daysObj) {
        const day = Object.keys(daysObj)[0]; // example: monday
        frequencyDay = day;

        notificationType[task] = daysObj[day]?.isEnabled || false;

        (daysObj[day]?.frequencyTime || []).forEach((t: string) => {
          const normalized = `${t}:00`;
          if (normalized in timePreference) {
            timePreference[normalized as TimePreferenceKey] = true;
          }
        });
      }
    });
  }

  const allDisabled =
    !notificationType.completedTask &&
    !notificationType.overDueTask &&
    !notificationType.upcomingTask;

  const Frequency: Frequency = allDisabled ? "none" : frequency;
  notificationType.overdueescalation = data?.isOverdueEscalationEnabled || false;

  return {
    timeZone: data.timeZone,
    frequency: Frequency,
    frequencyDay,
    timePreference,
    notificationType,
  };
}


const Tasks = () => {
    const [timeZone, setTimeZone] = useState("CST");
    const [frequency, setFrequency] = useState<Frequency>("daily");
    const [frequencyDay, setFrequencyDay] = useState("Mon");
    const [originalSettings, setOriginalSettings] = useState<any>(null);
    const { showSnackbar } = useDisplaySnackbar();
    const queryClient = useQueryClient();
    const [_showSpinner, setShowSpinner] = useState<boolean>(false);
    const dayMapUItoAPI: Record<string, string> = {
        Mon: "monday",
        Fri: "friday",
    };

    const dayMapAPItoUI: Record<string, string> = {
        monday: "Mon",
        friday: "Fri",
    };

    const [timePreference, setTimePreference] = useState<Record<TimePreferenceKey, boolean>>({
        "09:00:00": true,
        "12:00:00": false,
        "18:00:00": false,
    });
    const [notificationType, setNotificationType] = useState({
        completedTask: false,
        overDueTask: true,
        upcomingTask: false,
        overdueescalation: false,
    });

    const [validationErrors, setValidationErrors] = useState<{
        timeZone?: boolean;
        frequency?: boolean;
        timePreference?: boolean;
        notificationType?: boolean;
    }>({});
    const [showUnsavedChangesConfirm, setShowUnsavedChangesConfirm] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
    const [pendingRoute, setPendingRoute] = useState<string>("");
    const location = useLocation();
    const isEmailContentDisabled = frequency === "none";

    const isFormValid = () => {
        const notifTypeValid =
            frequency === "none" ||
            Object.values(notificationType).some(Boolean);

        // TimePreference validation 
        const timePrefValid =
            frequency === "none"
            ? true
            : frequency === "daily"
                ? Object.values(timePreference).some(Boolean)
                : frequency === "weekly"
                ? (frequencyDay && Object.values(timePreference).some(Boolean))
                : false;

        return (
            timeZone &&
            frequency &&
            notifTypeValid &&
            timePrefValid
        );
    };

    const {
        isLoading,
        isFetching,
        isSuccess,
        isError,
        error,
        data,
    } = IntegrationService.useGetUserNotificationSettings(true);

    useEffect(() => {
        if (isSuccess && data) {
            const settings = normalizeSettings(data);

            const uiSettings = {
                ...settings,
                frequencyDay: dayMapAPItoUI[settings.frequencyDay] || "",
            };

            setOriginalSettings(uiSettings);

            setTimeZone(uiSettings.timeZone || "CST");
            setFrequency(uiSettings.frequency);
            setFrequencyDay(uiSettings.frequencyDay);
            setTimePreference(uiSettings.timePreference);
            setNotificationType(uiSettings.notificationType);
        }
        if (isError && error) {
            const errorMessage =
                (error as any)?.data?.errors ||
                (error as any)?.data?.message;
            showSnackbar("error", errorMessage);
        }
    }, [data, error, isError, isSuccess, isLoading, isFetching]);

    const hasChanges = () => {
        if (!originalSettings) return false;
        return (
            originalSettings.timeZone !== timeZone ||
            originalSettings.frequency !== frequency ||
            originalSettings.frequencyDay !== frequencyDay ||
            JSON.stringify(originalSettings.timePreference) !== JSON.stringify(timePreference) ||
            JSON.stringify(originalSettings.notificationType) !== JSON.stringify(notificationType)
        );
    };
    const hasUnsavedChanges = useMemo(
      () => hasChanges(),
      [originalSettings, timeZone, frequency, frequencyDay, timePreference, notificationType]
    );

    const handleDiscard = () => {
        if (!originalSettings) return;

        setTimeZone(originalSettings.timeZone);
        setFrequency(originalSettings.frequency);
        setFrequencyDay(originalSettings.frequencyDay);
        setTimePreference(originalSettings.timePreference);
        setNotificationType(originalSettings.notificationType);
    };

    const handleFrequencyChange = (value: Frequency) => {
        setFrequency(value);

        if (value === "daily") {
            setTimePreference({
                "09:00:00": true,
                "12:00:00": false,
                "18:00:00": false,
            });
            // Daily → select all by default if coming from "none"
            if (originalSettings?.frequency === "none") {
                setNotificationType({
                    completedTask: true,
                    overDueTask: true,
                    upcomingTask: true,
                    overdueescalation: notificationType.overdueescalation,
                });
            } 
            // Restore original daily time preferences
            else if (originalSettings) {
                setTimePreference(originalSettings.timePreference);
                setFrequencyDay(originalSettings.frequencyDay);
            }
        }

        if (value === "weekly") {
            if (originalSettings?.frequency === "none") {
                setNotificationType({
                    completedTask: true,
                    overDueTask: true,
                    upcomingTask: true,
                    overdueescalation: notificationType.overdueescalation,
                });
            }
            const selectedTime = Object.entries(timePreference).find(([_, v]) => v)?.[0] || "09:00:00";
            setTimePreference({
                "09:00:00": false,
                "12:00:00": false,
                "18:00:00": false,
                [selectedTime]: true, // only one selected for weekly
            });

            if (!["Mon", "Fri"].includes(frequencyDay)) {
                setFrequencyDay("Mon");
            }
        }
        if (value === "none") {
            setNotificationType({
            completedTask: false,
            overDueTask: false,
            upcomingTask: false,
            overdueescalation: notificationType.overdueescalation
            });
        }
    };

    const updateMutation = IntegrationService.useUpdateUserNotificationSettings({
        onMutate: () => {
            setShowSpinner(true);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["get_user_notification_settings"] });
            showSnackbar("success", "Settings updated successfully");
            if (pendingNavigation) {
                pendingNavigation();
                setPendingNavigation(null);
                setPendingRoute("");
            }
        },
        onError: (err: any) => {
            const msg = err?.data?.message || "Failed to update settings";
            showSnackbar("error", msg);
        }
    });

    useEffect(() => {
        const guard = (nextRoute: string, proceed: () => void) => {
            const currentPath = `${location.pathname}${location.search}${location.hash}`;
            if (!hasUnsavedChanges || updateMutation.isPending || !nextRoute || nextRoute === currentPath) {
                proceed();
                return true;
            }
            setPendingRoute(nextRoute);
            setShowUnsavedChangesConfirm(true);
            setPendingNavigation(() => proceed);
            return false;
        };

        (window as any).__unsavedNavigationGuard = guard;
        return () => {
            if ((window as any).__unsavedNavigationGuard === guard) {
                delete (window as any).__unsavedNavigationGuard;
            }
        };
    }, [
        hasUnsavedChanges,
        updateMutation.isPending,
        location.pathname,
        location.search,
        location.hash,
    ]);

    useBeforeUnload(
        (event) => {
            if (!hasUnsavedChanges || updateMutation.isPending) return;
            event.preventDefault();
            event.returnValue = "";
        },
        { capture: true }
    );

    const handleSubmit = () => {
        if (!originalSettings) return;
        const errors = {
            timeZone: !timeZone,
            frequency: !frequency,
            notificationType:
            frequency === "none"
                ? false
                : !Object.values(notificationType).some(Boolean),
            frequencyDay: frequency === "weekly" ? !frequencyDay : false,  
            timePreference:
            frequency === "none"
                ? false
                : frequency === "daily"
                ? !Object.values(timePreference).some(Boolean)
                : frequency === "weekly"
                ? (!frequencyDay || !Object.values(timePreference).some(Boolean))
                : false,
        };

        setValidationErrors(errors);

        // If any error → do not submit
        if (Object.values(errors).some(Boolean)) return;

        // Build a diff payload
        const notificationsPayload: any[] = [];

        // 1️⃣ Check if email escalation changed
        if (notificationType.overdueescalation !== originalSettings.notificationType.overdueescalation) {
            notificationsPayload.push({
                frequency: "daily",
                frequencyDay: null,
                timePreference: { "09:00:00": true },
                notificationType: { overdueescalation: notificationType.overdueescalation },
            });
        }

        // 2️⃣ Check if main notifications or schedule changed
        const mainChanged =
            originalSettings.timeZone !== timeZone ||
            originalSettings.frequency !== frequency ||
            originalSettings.frequencyDay !== frequencyDay ||
            JSON.stringify({
                completedTask: notificationType.completedTask,
                overDueTask: notificationType.overDueTask,
                upcomingTask: notificationType.upcomingTask,
                // ignore escalation here
            }) !==
            JSON.stringify({
                completedTask: originalSettings.notificationType.completedTask,
                overDueTask: originalSettings.notificationType.overDueTask,
                upcomingTask: originalSettings.notificationType.upcomingTask,
            }) ||
            JSON.stringify(timePreference) !== JSON.stringify(originalSettings.timePreference);

        if (mainChanged) {
            const computedFrequency =
                frequency === "none"
                    ? originalSettings?.frequency && originalSettings.frequency !== "none"
                        ? originalSettings.frequency
                        : "daily"
                    : frequency;

            const mainNotification = {
                frequency: computedFrequency,
                frequencyDay: computedFrequency === "weekly" ? dayMapUItoAPI[frequencyDay] || frequencyDay : null,
                timePreference,
                notificationType: {
                    completedTask: notificationType.completedTask,
                    overDueTask: notificationType.overDueTask,
                    upcomingTask: notificationType.upcomingTask,
                },
            };

            notificationsPayload.push(mainNotification);
        }

        // 3️⃣ If nothing changed → do nothing
        if (notificationsPayload.length === 0) return;

        const payload = {
            timeZone,
            notifications: notificationsPayload,
        };
        updateMutation.mutate(payload as any);
    };
    const handleTimePrefToggle = (key: string) => {
        setTimePreference((prev: any) => ({ ...prev, [key]: !prev[key] }));
    };
    const handleContentToggle = (key: string) => {
        setNotificationType((prev: any) => ({ ...prev, [key]: !prev[key] }));
    };

    const formatTimeLabel = (time: string) => {
        const [hour] = time.split(":");
        let h = parseInt(hour);

        const suffix = h >= 12 ? "PM" : "AM";

        if (h === 0) h = 12;         // 00 → 12 AM
        else if (h > 12) h -= 12;    // 18 → 6 PM

        return `${h}:00 ${suffix}`;
    };

    if (isLoading || isFetching) 
        return (
          <Box
            className="group-by-task-loading"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <CircularProgress size={40} thickness={4} />
          </Box>
        );

    return (
        <Box className="task-container">
            {/* TIME ZONE */}
            <Box className="tasks-settings" >
                <Box sx={{ width: "250px", mt: 2 }}>
                    <Typography sx={{ fontWeight: 600 }}>Time Zone</Typography>
                    <Typography className="task-settings-text">
                        Used to schedule email delivery at the correct local time.
                    </Typography>
                </Box>

                <RadioGroup
                    value={timeZone}
                    onChange={(e) => setTimeZone(e.target.value)}>
                    <FormControlLabel
                    value="IST"
                    control={<Radio 
                        size="small" 
                        sx={{
                            "&.Mui-checked": {
                            color:  "#6036D6",
                            },
                        }}
                    />}
                    label={
                        <Box sx={{mt: 2}}>
                            <Typography className="task-settings-label">IST</Typography>
                            <Typography className="task-settings-text">
                               India Standard Time (UTC +5:30)
                            </Typography>
                        </Box>
                    }
                    labelPlacement="end"
                    />
                    <FormControlLabel
                    value="CST"
                    control={<Radio 
                        size="small" 
                        sx={{
                            "&.Mui-checked": {
                            color:  "#6036D6",
                            },
                        }}
                    />}
                    label={
                        <Box sx={{mt: 2}}>
                            <Typography className="task-settings-label">CST</Typography>
                            <Typography className="task-settings-text">
                               Central Standard Time (UTC -6)
                            </Typography>
                        </Box>
                    }
                    />
                </RadioGroup>
            </Box>

            <Divider sx={{mt: 2, mb: 1}}/>
            {/* FREQUENCY */}
            <Box className="tasks-settings" >
                <Box sx={{ width: "250px", mt: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Frequency</Typography>
                    <Typography className="task-settings-text">
                        How often would you like to receive updates?
                    </Typography>
                </Box>
                <RadioGroup
                    value={frequency}
                    onChange={(e) => handleFrequencyChange(e.target.value as Frequency)}
                >
                    <FormControlLabel 
                        value="daily" 
                        control={<Radio 
                            size="small" 
                            sx={{
                                color: validationErrors.frequency ? "#D9534F" : "",
                                "&.Mui-checked": {
                                color: validationErrors.frequency ? "#6036D6" : "#6036D6",
                                },
                            }}
                        />} 
                        label={<Typography className="task-settings-label">Daily</Typography>}
                    />
                    <FormControlLabel 
                        value="weekly" 
                        control={<Radio 
                            size="small" 
                            sx={{
                                color: validationErrors.frequency ? "#D9534F" : "",
                                "&.Mui-checked": {
                                color: validationErrors.frequency ? "#6036D6" : "#6036D6",
                                },
                            }}
                        />} 
                        label={<Typography className="task-settings-label">Weekly</Typography>}
                    />
                    <FormControlLabel
                        value="none"
                        control={<Radio 
                            size="small" 
                            sx={{
                                color: validationErrors.frequency ? "#D9534F" : "",
                                "&.Mui-checked": {
                                color: validationErrors.frequency ? "#6036D6" : "#6036D6",
                                },
                            }}
                        />}
                        label={
                            <Typography className="task-settings-label">No Email Content Updates</Typography>
                        }
                    />
                </RadioGroup>

            {/* DAILY OR WEEKLY: Delivery Time */}
            {frequency !== "none" && (
            <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>Delivery Time</Typography>
                <Typography className="task-settings-text">
                What time would you like to receive your update?
                </Typography>

                {frequency === "weekly" && (
                <Box sx={{ mt: 1 }}>
                    <FormGroup row sx={{ gap: 1 }}>
                    {["Mon", "Fri"].map((day) => (
                        <FormControlLabel
                        key={day}
                        control={
                            <Radio
                            size="small"
                            sx={{
                                color: validationErrors.frequency ? "#D9534F" : "",
                                "&.Mui-checked": {
                                color: validationErrors.frequency ? "#6036D6" : "#6036D6",
                                },
                            }}
                            checked={frequencyDay === day}
                            onChange={() => setFrequencyDay(day)}
                            />
                        }
                        label={day}
                        />
                    ))}
                    </FormGroup>
                </Box>
                )}

                {frequency === "daily" && (
                <FormGroup row>
                    {(Object.keys(timePreference) as TimePreferenceKey[]).map((key) => (
                    <FormControlLabel
                        key={key}
                        control={<Checkbox size="small" className="task-setting-checkbox"
                        sx={{
                            color: !Object.values(timePreference).some(Boolean) && validationErrors.timePreference
                                ? "#D9534F"
                                : "",
                            "&.Mui-checked": {
                                color: "#6036D6",
                            },
                        }}/>}
                        checked={timePreference[key]}
                        onChange={() => handleTimePrefToggle(key)}
                        label={formatTimeLabel(key)}
                    />
                    ))}
                </FormGroup>
                )}

                {frequency === "weekly" && (
                <RadioGroup
                    row
                    value={Object.entries(timePreference).find(([_, v]) => v)?.[0] || ""}
                    onChange={(e) => {
                    const selected = e.target.value as TimePreferenceKey;
                    setTimePreference({
                        "09:00:00": false,
                        "12:00:00": false,
                        "18:00:00": false,
                        [selected]: true, // only one selected
                    });
                    }}
                >
                    {(Object.keys(timePreference) as TimePreferenceKey[]).map((key) => (
                    <FormControlLabel
                        key={key}
                        value={key}
                        control={<Radio size="small" 
                            sx={{
                            color: !Object.values(timePreference).some(Boolean) && validationErrors.timePreference
                                ? "#D9534F"
                                : "",
                            "&.Mui-checked": {
                                color: "#6036D6",
                            },
                            }} />}
                        label={formatTimeLabel(key)}
                    />
                    ))}
                </RadioGroup>
                )}
            </Box>
            )}
            </Box>
            {/* EMAIL CONTENT */}
            <Box
              className="tasks-settings"
              sx={{
                opacity: isEmailContentDisabled ? 0.45 : 1,
                pointerEvents: isEmailContentDisabled ? "none" : "auto",
              }}
            >
                <Box sx={{ width: "250px", mt:2 }}>
                    <Typography sx={{ fontWeight: 600 }}>Email Content</Typography>
                    <Typography className="task-settings-text">
                        Choose which updates you'd like to receive.
                    </Typography>
                </Box>
                <FormGroup>
                    <FormControlLabel 
                        control={
                        <Checkbox 
                            size="small"
                            className="task-setting-checkbox"
                            disabled={isEmailContentDisabled}
                            checked={notificationType.overDueTask}
                            onChange={() => handleContentToggle("overDueTask")}
                            sx={{
                                color: !Object.values(notificationType).some(Boolean) && validationErrors.notificationType
                                ? "#D9534F" 
                                : "",
                                "&.Mui-checked": {
                                color: "#6036D6",
                                },
                            }}
                        />
                        }
                        label={
                        <Box sx={{mt: 2}}>
                            <Typography className="task-settings-label">Overdue Tasks</Typography>
                            <Typography className="task-settings-text">
                               Get notified about tasks that have passed their due date.
                            </Typography>
                        </Box>
                        }
                    />
                    <FormControlLabel 
                        control={
                            <Checkbox 
                            size="small" 
                            className="task-setting-checkbox"
                            disabled={isEmailContentDisabled}
                            checked={notificationType.upcomingTask}
                            onChange={() => handleContentToggle("upcomingTask")}
                            sx={{
                                color: !Object.values(notificationType).some(Boolean) && validationErrors.notificationType
                                ? "#D9534F" 
                                : "",
                                "&.Mui-checked": {
                                color: "#6036D6",
                                },
                            }}/>
                        } 
                        label={
                            <Box sx={{mt: 2}}>
                                <Typography className="task-settings-label">Upcoming Tasks</Typography>
                                <Typography className="task-settings-text">
                                   Receive reminders for tasks that are due soon.
                                </Typography>
                            </Box>
                        }
                    />
                    <FormControlLabel 
                        control={
                            <Checkbox 
                            size="small" 
                            className="task-setting-checkbox"
                            disabled={isEmailContentDisabled}
                            checked={notificationType.completedTask}
                            onChange={() => handleContentToggle("completedTask")}
                            sx={{
                                color: !Object.values(notificationType).some(Boolean) && validationErrors.notificationType
                                ? "#D9534F" 
                                : "", // normal color
                                "&.Mui-checked": {
                                color: "#6036D6",
                                },
                            }}
                            />
                        } 
                        label={
                            <Box sx={{mt: 2}}>
                                <Typography className="task-settings-label">Completed Tasks</Typography>
                                <Typography className="task-settings-text">
                                   Get a summary of all completed tasks.
                                </Typography>
                            </Box>
                        }
                    />
                </FormGroup>
            </Box>
            <Divider sx={{mt: 2, mb:1 }}/>
            <Box className="tasks-settings-email">
                <Box sx={{ width: "270px", mt: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Email Escalations</Typography>
                    <Typography className="task-settings-text">
                        Receive emails on escalation of Overdue Tasks.
                    </Typography>
                </Box>
                <Switch
                    checked={notificationType.overdueescalation}
                    onChange={() => handleContentToggle("overdueescalation")}
                    sx={{
                        mt: 1,
                        width: 56,
                        height: 26,
                        padding: 0,
                        "& .MuiSwitch-switchBase": {
                        padding: "3px",
                        "&.Mui-checked": {
                            transform: "translateX(28px)",
                            color: "#fff",
                            "& + .MuiSwitch-track": {
                            backgroundColor: "#6036D6",
                            opacity: 1,
                            },
                        },
                        },
                        "& .MuiSwitch-thumb": {
                        width: 20,
                        height: 20,
                        boxShadow: "none",
                        },
                        "& .MuiSwitch-track": {
                        borderRadius: "16px",
                        backgroundColor: "#D9D9E3",
                        opacity: 1,
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#6E6E80",
                        "&::after": {
                            content: '"OFF"',
                            position: "absolute",
                            right: 8,
                        },
                        },
                        "& .Mui-checked + .MuiSwitch-track::after": {
                        content: '"ON"',
                        left: 8,
                        right: "auto",
                        color: "#fff",
                        },
                    }}
                />
            </Box>
            <Divider sx={{mt: 2, mb:3 }}/>
            <Box sx={{ display: "flex", gap: 2 }}>
            <button
                onClick={handleSubmit}
                disabled={!hasUnsavedChanges}
                className="task-settings-save"
                style={{
                background: isFormValid() && hasUnsavedChanges ? "#6036D6" : "#F2F2F4",
                color: isFormValid() && hasUnsavedChanges ? "white" : "#A6A6B2",
                }}
            >
                Save Changes
            </button>

            {hasUnsavedChanges && (
                <button
                onClick={handleDiscard}
                className="task-settings-discard"
                >
                Discard
                </button>
            )}
            </Box>
            <CustomConfirmation
            isOpen={showUnsavedChangesConfirm}
            title="You have unsaved changes. Do you want to discard or save them?"
            subTitle={pendingRoute ? `Navigating to: ${pendingRoute}` : ""}
            width="380px"
            actionBtnLabel="Save"
            cancelBtnLabel="Discard"
            handleSuccess={() => {
                setShowUnsavedChangesConfirm(false);
                handleSubmit();
            }}
            handleClose={() => {
                setShowUnsavedChangesConfirm(false);
                handleDiscard();
                pendingNavigation?.();
                setPendingNavigation(null);
                setPendingRoute("");
            }}
            backgroundcolor
            actionType = "save"
        />
        </Box>
    );
};

export default Tasks;
